import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';
import { SendFriendInviteDto } from 'src/app/data-transfer/entities/send-friend-invite-dto';
import { FriendRequestResultStatus } from 'src/app/data-transfer/entities/friend-request-result-status';
import { FriendRequestApiService } from 'src/app/data-transfer/services/friend-request-api-service';
import { DialogActions } from 'src/app/model/dictionary';
import { SnackbarService } from 'src/app/services/snackbar.service';

@Component({
  selector: 'app-friend-request-dialog',
  templateUrl: './friend-request-dialog.component.html',
  styleUrl: './friend-request-dialog.component.scss',
})
export class FriendRequestDialogComponent implements OnInit {
  public organizationCode: string = '';
  public inviteForm: FormGroup;

  constructor(
    private dialogRef: MatDialogRef<FriendRequestDialogComponent>,
    private formBuilder: FormBuilder,
    private translateService: TranslocoService,
    private friendRequestApiService: FriendRequestApiService,
    private snackbarService: SnackbarService
  ) {
    this.inviteForm = this.formBuilder.group({
      inviteCode: ['', [Validators.required, this.mod97Validator()]],
    });
  }

  ngOnInit(): void {
    this.friendRequestApiService.getInviteCode(true).subscribe({
      next: (dto) => {
        this.organizationCode = dto.inviteCode;
      },
    });
  }

  sendFriendRequest(): void {
    if (this.inviteForm.invalid) {
      this.inviteForm.markAllAsTouched();
      return;
    }

    const inviteCodeValue = this.inviteForm.value.inviteCode;

    const payload: SendFriendInviteDto = {
      inviteCode: inviteCodeValue.trim(),
    };

    this.friendRequestApiService.sendFriendInvite(payload, true).subscribe({
      next: (response) => {
        let message = '';
        let duration = 1000;

        switch (response.status) {
          case FriendRequestResultStatus.Accepted:
            message = this.translateService.translate('friendRequests.addInviteDialog.inviteAcceptedMessage');
            this.friendRequestApiService.reloadPendingInvites(true);
            break;
          case FriendRequestResultStatus.Created:
            message = this.translateService.translate('friendRequests.addInviteDialog.inviteSentSuccessMessage');
            break;
          default:
            message = this.translateService.translate('friendRequests.addInviteDialog.inviteSentSuccessMessage');
            duration = 3000;
            break;
        }

        this.snackbarService.showInfo(message, duration);

        if (response.status === FriendRequestResultStatus.Accepted || response.status === FriendRequestResultStatus.Created) {
          this.dialogRef.close({ event: DialogActions.CONFIRM });
        }
      },
      error: (error) => {
        let message = '';

        switch (error.error.status) {
          case FriendRequestResultStatus.InvalidInviteCode:
            message = this.translateService.translate('friendRequests.addInviteDialog.invalidInviteCodeError');
            break;
          case FriendRequestResultStatus.OrganizationNotFound:
            message = this.translateService.translate('friendRequests.addInviteDialog.organizationNotFoundError');
            break;
          case FriendRequestResultStatus.SelfRequest:
            message = this.translateService.translate('friendRequests.addInviteDialog.selfRequestError');
            break;
          case FriendRequestResultStatus.AlreadyConnected:
            message = this.translateService.translate('friendRequests.addInviteDialog.alreadyConnectedError');
            break;
          case FriendRequestResultStatus.RequestAlreadyExists:
            message = this.translateService.translate('friendRequests.addInviteDialog.requestAlreadyExistsError');
            break;
          default:
            message = this.translateService.translate('friendRequests.addInviteDialog.unexpectedError');
            break;
        }
        this.snackbarService.showError(message);
      },
    });
  }

  private mod97Validator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const rawValue = control.value;

      if (rawValue === null || rawValue === undefined) {
        return null;
      }

      if (typeof rawValue !== 'string') {
        return { mod97Format: true };
      }

      const sanitizedValue = this.sanitizeInviteCode(rawValue);

      if (!sanitizedValue) {
        return rawValue ? { mod97Format: true } : null;
      }

      if (!/^[0-9]+$/.test(sanitizedValue)) {
        return { mod97Format: true };
      }

      if (sanitizedValue.length > 16) {
        return { mod97: true };
      }

      return this.CalculateMod97(sanitizedValue) === 1 ? null : { mod97: true };
    };
  }

  private sanitizeInviteCode(value: string): string {
    return (value.trim() ?? '').replace(/[-\s]/g, '').toUpperCase();
  }

  private CalculateMod97(value: string): number {
    const numericValue = BigInt(value);
    return Number(numericValue % 97n);
  }

  closeDialog() {
    this.dialogRef.close({ event: DialogActions.REJECT });
  }
}
